import React from 'react';
import { ToastContainer } from '@/components/ui';
import { useUI, useAppStore } from '@/store';
import { cn } from '@/utils';
import ChatScreen from '@/screens/ChatScreen';
import MatchCelebrationModal from '@/components/modals/MatchCelebrationModal';

export interface AppLayoutProps {
  children: React.ReactNode;
  className?: string;
  safeArea?: boolean;
  fullHeight?: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  className,
  safeArea = true,
  fullHeight = true
}) => {
  const { isModalOpen, modalType, modalData, closeModal, setModal, setActiveScreen } = useUI();
  const { matches } = useAppStore();

  const handleMatchCelebrationOpenChat = async () => {
    closeModal();

    // If we have matchId directly, use it
    if (modalData?.matchId) {
      const match = matches.items.find(m => m.id === modalData.matchId);

      if (match) {
        // Open chat with this match
        setModal('chat', { matchId: modalData.matchId, match });
      } else {
        // Match not loaded yet, set active match and open chat screen
        useAppStore.getState().setActiveMatch(modalData.matchId);
        setActiveScreen('chat');
      }
    } else if (modalData?.userId) {
      // Fallback: try to find match by user ID
      await new Promise(resolve => setTimeout(resolve, 300));

      const match = matches.items.find(m =>
        m.users.some(u => u.id === modalData.userId)
      );

      if (match) {
        // Open chat with this match
        setModal('chat', { matchId: match.id, match });
      } else {
        // Fallback - just open matches screen
        setActiveScreen('matches');
      }
    } else {
      // No data - just open matches screen
      setActiveScreen('matches');
    }
  };

  return (
    <div
      className={cn(
        'relative bg-tg-bg text-tg-text h-full',
        {
          'safe-area-y': safeArea,
        },
        className
      )}
    >
      {children}
      <ToastContainer />

      {/* Modals */}
      {isModalOpen && modalType === 'chat' && (
        <div className="fixed inset-0 z-50 bg-tg-bg">
          <ChatScreen />
        </div>
      )}

      {isModalOpen && modalType === 'match-celebration' && (
        <MatchCelebrationModal
          userId={modalData?.userId}
          onClose={closeModal}
          onOpenChat={handleMatchCelebrationOpenChat}
        />
      )}
    </div>
  );
};

export { AppLayout };
